import React, { FC } from "react";
import { Tooltip } from "antd";
import {
  LockOutlined,
  UserOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { StatusPill } from "./statusMeta";
import MilestoneRail from "./MilestoneRail";

const initialsOf = (name?: string) => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
};

const fmtDate = (d?: string) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

interface ProjectCardProps {
  record: any;
  onOpen: (record: any) => void;
}

const ProjectCard: FC<ProjectCardProps> = ({ record, onOpen }) => {
  const overdue =
    record?.projectStatus === "Active" &&
    record?.plannedEndDate &&
    dayjs(record.plannedEndDate).isBefore(dayjs(), "day");

  const tasksPct = Math.round(Number(record?.tasksCompletionPercentage) || 0);

  return (
    <div
      className="pms-project-card"
      onClick={() => onOpen(record)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen(record);
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <StatusPill status={record?.projectStatus} />
        {record?.lock && (
          <Tooltip title={`Locked by ${record?.lockedByUserName || "N/A"}`}>
            <LockOutlined style={{ color: "#8094ae" }} />
          </Tooltip>
        )}
      </div>

      {record?.projectGroupName && (
        <div className="pms-eyebrow" style={{ marginTop: 12 }}>
          {record.projectGroupName}
        </div>
      )}
      <div
        className="pms-card-title"
        style={!record?.projectGroupName ? { marginTop: 12 } : undefined}
        title={record?.projectTitle}
      >
        {record?.projectTitle}
      </div>

      <div className="pms-card-meta">
        <div className="pms-card-meta-row">
          <span className="pms-avatar">
            {initialsOf(record?.projectOwnerName)}
          </span>
          <span className="pms-meta-text">
            {record?.projectOwnerName || "Unassigned"}
          </span>
        </div>
        <div className="pms-card-meta-row">
          <EnvironmentOutlined className="pms-meta-icon" />
          <span className="pms-meta-text">{record?.siteName || "—"}</span>
        </div>
        <div className="pms-card-meta-row">
          <UserOutlined className="pms-meta-icon" />
          <span className="pms-meta-text">
            {record?.createdByUserName
              ? `Created by ${record.createdByUserName}`
              : "—"}
          </span>
        </div>
      </div>

      <div className="pms-card-figures">
        <div>
          <div className="pms-figure-value">
            {record?.completedPhases ?? 0}/{record?.totalPhases ?? 0}
          </div>
          <div className="pms-figure-caption">Phases</div>
        </div>
        <div>
          <div className="pms-figure-value">{tasksPct}%</div>
          <div className="pms-figure-caption">Tasks</div>
        </div>
        <div>
          <div className="pms-figure-value">
            {record?.completedIssues ?? 0}/{record?.totalIssues ?? 0}
          </div>
          <div className="pms-figure-caption">Issues</div>
        </div>
      </div>

      <div className="pms-card-dates">
        <span>{fmtDate(record?.plannedStartDate)}</span>
        <span className={overdue ? "pms-date-overdue" : undefined}>
          {overdue ? "Overdue · " : "→ "}
          {fmtDate(record?.plannedEndDate)}
        </span>
      </div>

      <MilestoneRail
        completed={record?.completedPhases}
        total={record?.totalPhases}
        status={record?.projectStatus}
      />
    </div>
  );
};

export default ProjectCard;
